/**
 * Cross-references, bookmarks and figure/table captions of `.chilldoc` — APM-CD2-d,
 * docs/plan-chilldoc-v2.md §4.
 *
 * Targets carry a stable `anchorId` (headings, bookmarks, caption paragraphs). A `crossRef`
 * node stores only `targetId` + `show`; its text ("mục 3.2", "Bảng 4") is **computed while
 * drawing** from the current document, so moving a section renumbers every reference to it,
 * and a deleted target shows as broken instead of the reference disappearing.
 *
 * Pure functions over a document-order sequence of `RefItem`s: the editor walks ProseMirror
 * nodes, the markdown head / `.docx` writer walk JSON (`refItemsOfJson`) — one numbering rule.
 */
/** What a reference shows. Page numbers are not offered: the web editor has no stable pages. */
export const CROSS_REF_SHOWS = ['text', 'number', 'both'];
export const CAPTION_KINDS = ['figure', 'table'];
/** Node types that are reference targets (and so carry `anchorId`). */
export const REF_TARGET_NODE_TYPES = ['heading', 'paragraph', 'bookmark'];
/** Labels written into files (markdown head, `.docx`) — documents here are Vietnamese. */
export const VI_REF_LABELS = {
    section: (number) => `mục ${number}`,
    figure: 'Hình',
    table: 'Bảng',
    broken: '⚠ tham chiếu hỏng',
};
/**
 * Numbers headings like a multilevel outline (`1`, `1.1`, `1.1.1`) and counts figures and
 * tables separately. Empty headings are skipped, as in the outline panel. Leading zero
 * segments are dropped, so a document whose first heading is H2 still starts at `1`.
 */
export function buildRefIndex(items) {
    const counters = [0, 0, 0, 0, 0, 0];
    let section = '';
    let figures = 0;
    let tables = 0;
    const targets = [];
    for (const item of items) {
        if (item.kind === 'heading') {
            const text = item.text.trim();
            if (!text)
                continue;
            const level = Math.min(6, Math.max(1, Math.round(item.level) || 1));
            counters[level - 1] += 1;
            for (let deeper = level; deeper < counters.length; deeper++)
                counters[deeper] = 0;
            const parts = counters.slice(0, level);
            while (parts.length > 1 && parts[0] === 0)
                parts.shift();
            section = parts.join('.');
            targets.push({ id: item.id, kind: 'heading', text, number: section, level, pos: item.pos });
        }
        else if (item.kind === 'bookmark') {
            targets.push({
                id: item.id,
                kind: 'bookmark',
                text: item.name,
                number: section,
                level: 0,
                pos: item.pos,
            });
        }
        else {
            const count = item.caption === 'figure' ? ++figures : ++tables;
            targets.push({
                id: item.id,
                kind: item.caption,
                text: item.text.trim(),
                number: String(count),
                level: 0,
                pos: item.pos,
            });
        }
    }
    const byId = new Map();
    for (const target of targets) {
        if (target.id && !byId.has(target.id))
            byId.set(target.id, target);
    }
    return { targets, byId };
}
/** `"Hình 3"` / `"Bảng 2"` — the label drawn before a caption's text. */
export function captionLabel(kind, number, labels) {
    return `${kind === 'figure' ? labels.figure : labels.table} ${number}`;
}
/** The number part of a target as a reference reads it: "mục 3.2", "Hình 4". */
function numberLabel(target, labels) {
    if (target.kind === 'figure' || target.kind === 'table') {
        return captionLabel(target.kind, target.number, labels);
    }
    return target.number ? labels.section(target.number) : target.text;
}
/** Text a `crossRef` shows right now; a missing target ⇒ the broken label, never empty. */
export function crossRefText(index, targetId, show, labels) {
    const target = typeof targetId === 'string' && targetId ? index.byId.get(targetId) : undefined;
    if (!target)
        return { text: labels.broken, broken: true };
    const mode = crossRefShowOf(show);
    const number = numberLabel(target, labels);
    if (mode === 'number')
        return { text: number, broken: false };
    if (mode === 'text')
        return { text: target.text || number, broken: false };
    return {
        text: target.text && target.text !== number ? `${number}: ${target.text}` : number,
        broken: false,
    };
}
/** Unknown / legacy values (`page`) read as `number`. */
export function crossRefShowOf(value) {
    return CROSS_REF_SHOWS.includes(value) ? value : 'number';
}
export function captionKindOf(value) {
    return CAPTION_KINDS.includes(value) ? value : null;
}
export function anchorIdOf(attrs) {
    const id = attrs?.['anchorId'];
    return typeof id === 'string' && id !== '' ? id : null;
}
function plainText(node) {
    if (node.type === 'text')
        return node.text ?? '';
    return (node.content ?? []).map(plainText).join('');
}
/** `RefItem`s of a stored (JSON) document, in document order. */
export function refItemsOfJson(doc) {
    const items = [];
    const visit = (node) => {
        if (node.type === 'heading') {
            items.push({
                kind: 'heading',
                id: anchorIdOf(node.attrs),
                level: Number(node.attrs?.['level'] ?? 1),
                text: plainText(node),
            });
        }
        else if (node.type === 'bookmark') {
            items.push({
                kind: 'bookmark',
                id: anchorIdOf(node.attrs),
                name: String(node.attrs?.['name'] ?? ''),
            });
            return;
        }
        else if (node.type === 'paragraph') {
            const caption = captionKindOf(node.attrs?.['caption']);
            if (caption) {
                items.push({ kind: 'caption', caption, id: anchorIdOf(node.attrs), text: plainText(node) });
            }
        }
        for (const child of node.content ?? [])
            visit(child);
    };
    visit(doc);
    return items;
}
/** Index of a stored document. */
export function refIndexOfJson(doc) {
    return buildRefIndex(refItemsOfJson(doc));
}
/**
 * The document as a reader sees it, for writers that only know text (the `.docx` patcher):
 * a `crossRef` becomes a text node with its current text (keeping its marks), a caption
 * paragraph gets its "Hình 2: " label as leading text, and bookmarks are dropped.
 */
export function inlineRefsAsText(doc, labels = VI_REF_LABELS) {
    const index = refIndexOfJson(doc);
    const visit = (node) => {
        if (node.type === 'bookmark')
            return null;
        if (node.type === 'crossRef') {
            const { text } = crossRefText(index, node.attrs?.['targetId'], node.attrs?.['show'], labels);
            return { type: 'text', text, ...(node.marks?.length ? { marks: node.marks } : {}) };
        }
        const kind = node.type === 'paragraph' ? captionKindOf(node.attrs?.['caption']) : null;
        const target = kind ? index.byId.get(anchorIdOf(node.attrs) ?? '') : undefined;
        if (!node.content && !target)
            return node;
        const content = (node.content ?? [])
            .map(visit)
            .filter((child) => child !== null);
        if (kind && target) {
            content.unshift({ type: 'text', text: `${captionLabel(kind, target.number, labels)}: ` });
        }
        return { ...node, content };
    };
    return visit(doc) ?? doc;
}
//# sourceMappingURL=chilldoc-refs.js.map